import React, { useEffect, useMemo } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { IoMdArrowRoundBack } from "react-icons/io";
import { LineWave } from "react-loader-spinner";
import { toast } from "react-toastify";
import {
  addUpcomingFeature,
  clearCurrentFeature,
  getAllUpcomingFeatures,
  updateUpcomingFeature,
} from "../../../redux/slices/upcomingFeatureSlice";
import "./AddFeatureDropEditor.css";

const Font = ReactQuill.Quill.import("formats/font");
Font.whitelist = ["poppins"];
ReactQuill.Quill.register(Font, true);

const formSchema = Yup.object().shape({
  title: Yup.string().trim().required("Heading is required"),
  description: Yup.string().trim().required("System message is required"),
  isActive: Yup.boolean().required("Status is required"),
});

const initialValues = {
  title: "",
  description: "",
  isActive: true,
};

const fieldClass =
  "mt-1 block w-full px-3 py-2 bg-[#372F5B] rounded-md shadow-sm focus:outline-none focus:ring focus:ring-red-500 focus:ring-opacity-50 text-white";

const editorModules = {
  toolbar: [
    [{ font: ["poppins"] }, { size: ["small", false, "large", "huge"] }],
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    [{ list: "bullet" }, { list: "ordered" }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    ["blockquote"],
    ["image", "link"],
    ["clean"],
  ],
};

const editorFormats = [
  "font",
  "size",
  "header",
  "list",
  "bullet",
  "bold",
  "italic",
  "underline",
  "strike",
  "script",
  "color",
  "background",
  "align",
  "blockquote",
  "code-block",
  "link",
  "image",
];

export default function AddFeatureDrop() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { features, currentFeature, isSaving, isLoading } = useSelector(
    (state) => state.upcomingFeatureReducer,
  );

  useEffect(() => {
    if (isEdit && features.length === 0 && !currentFeature) {
      dispatch(getAllUpcomingFeatures());
    }
  }, [dispatch, isEdit, features.length, currentFeature]);

  useEffect(() => {
    return () => {
      dispatch(clearCurrentFeature());
    };
  }, [dispatch]);

  const selectedFeature = useMemo(() => {
    if (!isEdit) return null;
    if (currentFeature?.id === id) return currentFeature;
    return features.find((item) => item.id === id) || null;
  }, [isEdit, id, currentFeature, features]);

  const formInitialValues = useMemo(() => {
    if (!selectedFeature) return initialValues;
    return {
      title: selectedFeature.title || "",
      description: selectedFeature.description || "",
      isActive: Boolean(selectedFeature.isActive),
    };
  }, [selectedFeature]);

  return (
    <div className="pb-[60px] text-white">
      <div className="flex items-start">
        <Link
          to="/panel/feature-drops"
          className="inline-block text-white pb-5 text-3xl"
        >
          <IoMdArrowRoundBack />
        </Link>
      </div>

      <div className="max-w-4xl mx-auto p-6 bg-[#211849] shadow-md rounded-lg text-left text-white">
        <h2 className="text-2xl font-bold mb-8">
          {isEdit ? "Edit System Message" : "Add System Message"}
        </h2>

        <Formik
          enableReinitialize
          initialValues={formInitialValues}
          validationSchema={formSchema}
          onSubmit={(values) => {
            const payload = {
              ...values,
              isActive:
                typeof values.isActive === "boolean"
                  ? values.isActive
                  : values.isActive === "true",
            };
            const request = isEdit
              ? dispatch(updateUpcomingFeature({ id, payload }))
              : dispatch(addUpcomingFeature(payload));

            request.then((response) => {
              if (
                addUpcomingFeature.fulfilled.match(response) ||
                updateUpcomingFeature.fulfilled.match(response)
              ) {
                toast.success(
                  isEdit
                    ? "System message updated successfully"
                    : "System message created successfully",
                );
                navigate("/panel/feature-drops");
                return;
              }

              toast.error(
                response?.payload?.response?.data?.message ||
                  response?.payload?.message ||
                  "Something went wrong",
              );
            });
          }}
        >
          {({ values, setFieldValue, setFieldTouched, touched, errors }) => (
            <Form>
              <div className="mb-5">
                <label htmlFor="title" className="block text-sm font-medium">
                  Heading <span className="text-red-500">*</span>
                </label>
                <Field
                  type="text"
                  id="title"
                  name="title"
                  placeholder="Write the heading..."
                  className={fieldClass}
                />
                <ErrorMessage
                  name="title"
                  component="p"
                  className="text-red-500 text-sm mt-1"
                />
              </div>

              <div className="mb-5">
                <label htmlFor="description" className="block text-sm font-medium">
                  System Message <span className="text-red-500">*</span>
                </label>
                <div
                  className={`quill-dark-editor mt-1 ${
                    touched.description && errors.description
                      ? "quill-dark-editor-error"
                      : ""
                  }`}
                >
                  <ReactQuill
                    theme="snow"
                    value={values.description}
                    onChange={(value) => setFieldValue("description", value)}
                    onBlur={() => setFieldTouched("description", true)}
                    modules={editorModules}
                    formats={editorFormats}
                    placeholder="Enter the system message here..."
                  />
                </div>
                <ErrorMessage
                  name="description"
                  component="p"
                  className="text-red-500 text-sm mt-1"
                />
              </div>

              <div className="mb-6">
                <label htmlFor="isActive" className="block text-sm font-medium">
                  Status <span className="text-red-500">*</span>
                </label>
                <Field as="select" id="isActive" name="isActive" className={fieldClass}>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </Field>
                <ErrorMessage
                  name="isActive"
                  component="p"
                  className="text-red-500 text-sm mt-1"
                />
              </div>

              <div className="flex justify-end items-center gap-2">
                <button
                  type="button"
                  className="text-white bg-gray-600 px-4 py-2 rounded-md hover:bg-gray-700"
                  onClick={() => navigate("/panel/feature-drops")}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="text-white bg-red-500 px-4 py-2 rounded-md hover:bg-red-600 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isSaving
                    ? "Saving..."
                    : isEdit
                      ? "Update System Message"
                      : "Create System Message"}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>

      {(isLoading || isSaving) && (
        <div className="loader">
          <LineWave color={"#fff"} loading={isLoading || isSaving} size={150} />
        </div>
      )}
    </div>
  );
}
